/**
 * @file Pending Abra (`#/pending`) — admin only.
 *
 * The "Gear took an action, waiting for Abra to catch up" queue. Every row
 * here corresponds to a /pending_changes doc submitted via the device-detail
 * action menu (handover / takeback / transfer / make testing / sell / dispose)
 * or the person-page "Hand over a device" dialog (register — a new laptop or
 * phone Abra doesn't have yet; reconciles by serial, so it has no device
 * link until Finance creates the asset).
 *
 *   • Pending          — submitted; not yet reflected in Abra.
 *   • Reconciled       — a sync detected Abra now matches the expected state.
 *                        Shown for 24h as a confirmation, then auto-hidden.
 *   • Cancelled        — admin cancelled; left out of this view (audit log has it).
 *
 * No per-row "I fixed it" button — the next sync reconciles automatically
 * when Abra actually changes. Admins can Cancel a row if they made a
 * mistake (a follow-up "scratch that" goes to Slack so Finance doesn't
 * keep working on it).
 *
 * Sales: a CSV export at the top of the page gives Finance the row they'd
 * paste into the Public Invoices sheet. Per user, that sheet can't be
 * shared with the service account, so the loop is manual: export CSV,
 * paste into the sheet, mark device sold in Abra.
 */

import { el, clear, formatDate, toast } from '../utils.js';
import { Icons } from '../icons.js';
import { navigate } from '../router.js';
import { watchPendingChanges } from '../db.js';
import { cancelGearAction, retrySlackNow } from '../api.js';
import { downloadFinanceCsv } from '../lib/finance-csv.js';
import { renderLoadError } from '../lib/load-error.js';
import { copyable } from '../lib/copyable.js';
import { promptText } from '../lib/text-modal.js';
import { isTestMode } from '../test-mode.js';
import { renderPageHead } from '../page-header.js';
import { GEAR_ACTIONS, GEAR_ACTION_LABELS, gearActionLabel } from '../lib/gear-actions.js';
import { sortableTable } from '../lib/sortable-table.js';

export function renderPending(host) {
  clear(host);

  host.appendChild(renderPageHead(
    'Pending Abra',
    "Actions taken in Gear that are waiting for Abra to catch up. Each row pinged Slack so Finance can do the matching update in Abra. When the next sync sees Abra reflect the change, the row reconciles automatically and disappears after 24h. Cancel a row only if you submitted it by mistake — that posts a follow-up to Slack.",
  ));

  // ── Filter bar ──────────────────────────────────────────────────────────
  const state = { status: 'pending', action: '', rows: [] };
  const statusSeg = el('div', { class: 'segment' });
  for (const [k, label] of [['pending', 'Pending'], ['all', 'All (incl. reconciled)']]) {
    const btn = el('button', { class: state.status === k ? 'active' : null }, label);
    btn.addEventListener('click', () => {
      state.status = k;
      for (const b of statusSeg.children) b.classList.toggle('active', b === btn);
      rerender();
    });
    statusSeg.appendChild(btn);
  }

  const actionSelect = el('select', null,
    el('option', { value: '' }, 'All action types'),
    ...GEAR_ACTIONS.map((k) =>
      el('option', { value: k }, GEAR_ACTION_LABELS[k])),
  );
  actionSelect.addEventListener('change', () => {
    state.action = actionSelect.value;
    rerender();
  });

  const exportSalesBtn = el('button', { class: 'btn-outline btn' }, 'Export sales CSV');
  exportSalesBtn.addEventListener('click', () => exportFinanceCsv(state.rows, 'sell'));

  const exportLootBtn = el('button', { class: 'btn-outline btn' }, 'Export loot CSV');
  exportLootBtn.addEventListener('click', () => exportFinanceCsv(state.rows, 'loot'));

  // Manual "Retry failed Slack now" — the scheduled cron runs every 10
  // min, but admins sometimes want to drain right after fixing a webhook.
  const retryBtn = el('button', { class: 'btn-outline btn' }, 'Retry failed Slack');
  // The queue is inherently production (real rows → real webhook), so the
  // callable refuses in test mode; disable the control to match.
  if (isTestMode()) {
    retryBtn.disabled = true;
    retryBtn.title = 'Disabled in test mode — the Slack queue is production-only.';
  }
  retryBtn.addEventListener('click', async () => {
    retryBtn.disabled = true;
    const original = retryBtn.textContent;
    retryBtn.textContent = 'Retrying…';
    try {
      const res = await retrySlackNow();
      if (res?.retried) {
        toast(`Retried ${res.retried} · ${res.succeeded} sent · ${res.failed} still failing.`,
              res.failed ? 'error' : 'success');
      } else {
        toast('Nothing to retry — queue is clean.', 'info');
      }
    } catch (err) {
      toast(err.message || 'Retry failed.', 'error');
    } finally {
      retryBtn.disabled = false;
      retryBtn.textContent = original;
    }
  });

  host.appendChild(el('div', { class: 'toolbar', style: 'gap:12px;flex-wrap:wrap' },
    statusSeg, actionSelect, exportSalesBtn, exportLootBtn, retryBtn,
  ));

  const tableHost = el('div', { class: 'card', style: 'padding:0' });
  host.appendChild(tableHost);

  function rerender() {
    clear(tableHost);
    const filtered = applyFilters(state.rows, state);
    if (!filtered.length) {
      tableHost.appendChild(el('div', { class: 'empty' },
        Icons.inbox(),
        el('p', null, state.status === 'pending'
          ? 'No pending actions — Gear and Abra agree.'
          : 'No actions match the current filters.'),
      ));
      return;
    }
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'When',    sortValue: (r) => r.ts },
        { label: 'Action',  sortValue: (r) => gearActionLabel(r.action) },
        { label: 'Device',  sortValue: (r) => r.device?.name || r.device?.sn },
        { label: 'Details', sortable: false },
        { label: 'Status',  sortValue: (r) => r.status },
        { label: '', sortable: false, thStyle: 'width:120px;text-align:right' },
      ],
      rows: filtered,
      renderRow,
      initialSort: { index: 0, dir: 'desc' }, // newest first
    }));
  }

  function renderRow(r) {
    const when = r.ts?.toDate?.() ? formatDate(r.ts.toDate()) : '—';
    const reconciledLabel = r.reconciledVia === 'abra_removed' ? 'Resolved (Abra removed)'
      : r.reconciledVia === 'superseded' ? 'Superseded'
      : 'Reconciled';
    const reconciledTitle = r.reconciledVia === 'abra_removed'
      ? 'Resolved automatically: the device was removed from Abra (sold/disposed) by Finance.'
      : r.reconciledVia === 'superseded'
        ? 'Superseded by a newer action submitted on the same device.'
        : 'Reconciled: Abra now matches the expected state.';
    const statusChip = r.status === 'pending'
      ? el('span', { class: 'chip chip-mismatch' }, 'Pending')
      : r.status === 'reconciled'
        ? el('span', { class: 'chip chip-verified', title: reconciledTitle }, reconciledLabel)
        : el('span', { class: 'chip' }, r.status);
    return el('tr', null,
      el('td', { class: 'muted' }, when),
      el('td', null, gearActionLabel(r.action)),
      // A register row has no device page until Abra creates the asset.
      el('td', r.deviceId ? { onClick: () => navigate(`/devices/${r.deviceId}`), style: 'cursor:pointer', 'data-row-link': true } : null,
        el('div', null, r.device?.name ? copyable(r.device.name, { label: 'name' }) : '—'),
        r.device?.sn
          ? el('div', { class: 'muted' }, copyable(r.device.sn, { label: 'SN', mono: true }))
          : null,
      ),
      el('td', null, describeDetails(r)),
      el('td', null, statusChip,
        r.slack && r.slack.attempted && !r.slack.ok
          ? el('div', { class: 'muted', style: 'font-size:var(--fs-small);margin-top:4px;color:var(--orange)' },
              `Slack failed: ${(r.slack.error || r.slack.reason || '?').slice(0, 60)}`)
          : null,
      ),
      el('td', { style: 'text-align:right' },
        r.status === 'pending'
          ? el('button', {
              class: 'btn-ghost btn btn-sm',
              onClick: async () => {
                const reason = await promptText({
                  title: 'Cancel pending change', label: 'Note for Finance', placeholder: 'Optional',
                  confirmLabel: 'Cancel change', cancelLabel: 'Keep', multiline: true,
                });
                if (reason === null) return;
                try {
                  const res = await cancelGearAction({ pendingId: r.id, reason: reason || null });
                  if (res?.slack?.ok) toast('Cancelled. Slack notified.', 'success');
                  else toast(`Cancelled, BUT Slack delivery failed — ${res?.slack?.error || res?.slack?.reason || 'unknown'}. Will retry every 10 min.`, 'error', 8000);
                } catch (err) {
                  toast(err.message || 'Cancel failed.', 'error');
                }
              },
            }, 'Cancel')
          : null,
      ),
    );
  }

  const unsub = watchPendingChanges({}, (rows) => {
    state.rows = rows;
    rerender();
  }, () => {
    clear(tableHost);
    tableHost.appendChild(renderLoadError('Could not load the pending queue.'));
  });
  host.addEventListener('cleanup', unsub, { once: true });
}

function applyFilters(rows, state) {
  const now = Date.now();
  return rows.filter((r) => {
    if (r.status === 'cancelled') return false;
    if (state.status === 'pending' && r.status !== 'pending') return false;
    if (state.status === 'all' && r.status === 'reconciled') {
      // Hide reconciled rows older than 24h to keep the list focused.
      if (r.reconciledAt?.toMillis && now - r.reconciledAt.toMillis() > 24 * 60 * 60 * 1000) return false;
    }
    if (state.action && r.action !== state.action) return false;
    return true;
  });
}

function describeDetails(r) {
  const parts = [];
  if (r.toHolder?.name)   parts.push(`→ ${r.toHolder.name}`);
  if (r.fromHolder?.name) parts.push(`← ${r.fromHolder.name}`);
  if (r.saleData) {
    const buyer = r.saleData.buyer?.name || r.saleData.buyer?.email || 'buyer';
    const price = r.saleData.salePriceCzk != null ? `${r.saleData.salePriceCzk} CZK` : '(no price)';
    const ext = r.saleData.isExternal ? ' (external)' : '';
    parts.push(`${buyer}${ext} · ${price}`);
  }
  if (r.note) parts.push(`"${r.note}"`);
  const submittedBy = r.actor?.email ? el('div', { class: 'muted', style: 'font-size:var(--fs-small);margin-top:2px' }, `by ${r.actor.email}`) : null;
  return el('div', null,
    parts.length ? el('div', null, parts.join(' · ')) : el('span', { class: 'muted' }, '—'),
    submittedBy,
  );
}

// ── Finance CSV export ───────────────────────────────────────────────────
// Wire format + downloader live in src/lib/finance-csv.js so the
// offboarding flow can call the same code for its single-row, post-submit
// auto-download. We just map pending_changes rows into the shape that
// helper expects.

function exportFinanceCsv(rows, variant) {
  // STILL-PENDING only. Reconciled rows are work Finance already invoiced
  // (that's what reconciled means — Abra caught up), so including them made
  // every export re-send old sales and risked double-invoicing.
  const matching = rows.filter((r) => r.action === variant && r.status === 'pending');
  if (!matching.length) {
    toast(`No open ${gearActionLabel(variant).toLowerCase()} entries to export — already-reconciled ones are excluded.`, 'info');
    return;
  }
  const csvRows = matching.map((r) => {
    const s = r.saleData || {};
    return {
      deviceName:   r.device?.name || '',
      deviceSn:     r.device?.sn   || '',
      salePriceCzk: s.salePriceCzk,
      buyerEmail:   s.buyer?.email || '',
    };
  });
  const filename = downloadFinanceCsv(csvRows, variant);
  toast(`Exported ${matching.length} ${variant === 'sell' ? 'sale' : 'loot'}${matching.length === 1 ? '' : 's'} as ${filename}.`, 'success');
}
